import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useTranslation } from 'react-i18next';
import { FinanceCompositionDto } from '../../../../common/interfaces/invoicing.interface';
import { formatMoney } from '../../../../utilities/helper/money.helper';
import { cardSx } from './DebtDonut';

const TOP = 4;

/** The four biggest items, and the rest - manual lines among them - as one "other". */
export const compositionSlices = (composition: FinanceCompositionDto) => {
  const named = composition.items.filter(item => item.name !== null);
  const top = named.slice(0, TOP);
  const rest = composition.items.filter(item => !top.includes(item)).reduce((sum, item) => sum + Math.round(item.amount * 100), 0) / 100;
  return [
    ...top.map(item => ({ name: item.name as string, amount: item.amount, other: false })),
    ...(rest > 0 ? [{ name: '', amount: rest, other: true }] : [])
  ];
};

/**
 * What the month's issued invoices were for, by catalogue item. Discounts lower the total
 * but are not a slice: a ring of what was charged with a negative part in it would not add
 * up to anything.
 */
const CompositionDonut: React.FC<{ composition: FinanceCompositionDto | null; monthLabel: string }> = ({ composition, monthLabel }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const slices = composition ? compositionSlices(composition) : [];
  const total = composition?.total ?? 0;
  const colors = theme.palette.chart.mix;
  const colorOf = (index: number, other: boolean) => (other ? theme.palette.field.disabled : colors[index % colors.length]);
  const share = (amount: number) => (total > 0 ? Math.round((amount / total) * 100) : 0);
  const whole = new Intl.NumberFormat('sr-RS', { maximumFractionDigits: 0 }).format(total);

  return (
    <Box component="section" aria-labelledby="composition-title" sx={cardSx}>
      <Typography id="composition-title" component="h2" sx={{ fontSize: 16, fontWeight: 600, mb: 2 }}>
        {t('finance.composition.title', { month: monthLabel })}
      </Typography>
      {!composition || total <= 0 ? (
        <Typography sx={{ fontSize: 14, color: 'text.secondary', py: 4 }}>
          {t('finance.composition.empty', { month: monthLabel })}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box
            role="img"
            aria-label={t('finance.composition.aria', {
              parts: slices.map(slice => `${slice.other ? t('finance.composition.other') : slice.name} ${share(slice.amount)} %`).join(', ')
            })}
            sx={{ position: 'relative', width: 156, height: 156, flexShrink: 0 }}
          >
            <PieChart
              width={156}
              height={156}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              series={[
                {
                  data: slices.map((slice, index) => ({ id: index, value: slice.amount, color: colorOf(index, slice.other) })),
                  innerRadius: 56,
                  outerRadius: 78,
                  paddingAngle: 1
                }
              ]}
              slotProps={{ legend: { hidden: true } }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none'
              }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{whole}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('finance.composition.center')}</Typography>
            </Box>
          </Box>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, flex: 1, minWidth: 200 }}>
            {slices.map((slice, index) => (
              <Box component="li" key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, fontSize: 14 }}>
                <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: colorOf(index, slice.other), flexShrink: 0 }} />
                <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                  {slice.other ? t('finance.composition.other') : slice.name}
                </Box>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {share(slice.amount)} %
                </Box>
                <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 90, textAlign: 'right' }}>
                  {formatMoney(slice.amount)}
                </Box>
              </Box>
            ))}
            {composition.discountTotal < 0 && (
              <Box
                component="li"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, fontSize: 14, borderTop: 1, borderColor: 'line', mt: 0.5 }}
              >
                <Box component="span" sx={{ width: 10, flexShrink: 0 }} />
                <Box component="span" sx={{ flex: 1 }}>
                  {t('finance.composition.discounts')}
                </Box>
                <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', color: 'success.main', minWidth: 90, textAlign: 'right' }}>
                  −{formatMoney(Math.abs(composition.discountTotal))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 2 }}>{t('finance.composition.note')}</Typography>
    </Box>
  );
};

export default CompositionDonut;
